import { motion } from 'framer-motion';
import { containerVariants, fadeUpVariants } from '../lib/motion';
import { Rocket, Play, Sliders, TrendingUp } from 'lucide-react';

export default function HowItWorks() {
  const pillars = [
    {
      id: 'deploy',
      icon: Rocket,
      title: 'DEPLOY',
      desc: 'Launch mobility services across delivery networks, campuses and commute ecosystems.',
      color: 'bg-brand-mint text-brand-dark',
    },
    {
      id: 'operate',
      icon: Play,
      title: 'OPERATE',
      desc: 'Run vehicles, riders and day-to-day mobility operations through a connected ecosystem.',
      color: 'bg-brand-lavender text-brand-dark',
    },
    {
      id: 'manage',
      icon: Sliders,
      title: 'MANAGE',
      desc: 'Technology, IoT and real-time intelligence connect every vehicle and mobility operation.',
      color: 'bg-brand-lime text-brand-dark',
    },
    {
      id: 'monetize',
      icon: TrendingUp,
      title: 'MONETIZE',
      desc: 'Enable vehicle owners, hosts and leasing partners to participate in and earn from mobility operations.',
      color: 'bg-brand-mint text-brand-dark',
    },
  ];

  return (
    <section className="section-pad bg-white border-t border-brand-dark/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Column: Copy & Steps */}
          <div className="lg:col-span-7">
            <span className="text-xs font-bold text-brand-green uppercase tracking-wider bg-brand-mint px-3 py-1 rounded-full">
              Connected Platform
            </span>
            <h2 className="heading-display text-3xl md:text-5xl text-brand-dark mt-4 font-bold">
              One Platform for Every Mobility Need
            </h2>
            <p className="mt-4 text-brand-dark/70 text-base md:text-lg max-w-2xl leading-relaxed">
              From vehicles to operations and revenue, E Drives brings the mobility ecosystem together on one connected platform.
            </p>

            <motion.div 
              variants={containerVariants(0.1)}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="mt-10 space-y-6"
            >
              {pillars.map((p) => {
                const Icon = p.icon;
                return (
                  <motion.div 
                    key={p.id} 
                    variants={fadeUpVariants(20, 0.5)}
                    className="flex gap-4 items-start group"
                  >
                    <div className={`w-10 h-10 rounded-xl ${p.color} flex items-center justify-center shrink-0 shadow-sm border border-brand-dark/5 group-hover:scale-105 transition-transform duration-300`}>
                      <Icon size={18} />
                    </div>
                    <div>
                      <h3 className="font-bold text-xs tracking-wider text-brand-green uppercase">
                        {p.title}
                      </h3>
                      <p className="text-sm text-brand-dark/75 mt-1 leading-relaxed max-w-xl">
                        {p.desc}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>

          {/* Right Column: Platform Image */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-xl group">
              <img 
                src="/platform_dashboard_mockup.jpg" 
                alt="E Drives Connected Mobility Platform Flow" 
                className="w-full h-auto block group-hover:scale-[1.01] transition-transform duration-500 rounded-3xl" 
              />
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
